import { isValidPhoneNumber } from 'libphonenumber-js'
import * as yup from 'yup'
import { setLocale } from 'yup'

// Extend yup types
declare module 'yup' {
  interface StringSchema {
    iban(message?: string): StringSchema
    bic(message?: string): StringSchema
  }
}

// Simple email regex - requires @ and TLD with at least 2 letters
const EMAIL_REGEX = /^.+@.+\.[a-z]{2,}$/i

// Add email validation
yup.addMethod(yup.string, 'email', function (message = 'Enter a valid email address.') {
  return this.test('email', message, function (value) {
    if (!value) return true // Handle optional emails
    if (value.length > 320) return false // RFC 3696
    return EMAIL_REGEX.test(value)
  })
})

// Add phone validation using libphonenumber-js
yup.addMethod(yup.string, 'phone', function (message = 'Enter a valid phone number.') {
  return this.test('phone', message, function (value) {
    if (!value) return true // Handle optional phone numbers
    try {
      return isValidPhoneNumber(value, { defaultCountry: 'DE' })
    } catch (error) {
      return false
    }
  })
})

// IBAN validation function based on https://stackoverflow.com/a/35599724/5736491
function validateIBAN(iban: string): boolean {
  // Remove spaces and convert to uppercase
  const code = iban.replace(/\s/g, '').toUpperCase()

  // Check for basic format: 2 letters, 2 digits, then alphanumeric
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(code)) {
    return false
  }

  // Check length based on country code
  const lengths: Record<string, number> = {
    AD: 24,
    AE: 23,
    AL: 28,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    BY: 28,
    CH: 21,
    CR: 22,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    EG: 29,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GE: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LC: 32,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26,
    UA: 29,
    VG: 24,
    XK: 20
  }

  const countryCode = code.slice(0, 2)
  const expectedLength = lengths[countryCode]

  if (!expectedLength || code.length !== expectedLength) {
    return false
  }

  // Move first 4 characters to end and replace letters with numbers
  const rearranged = code.slice(4) + code.slice(0, 4)
  const numericString = rearranged.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString())

  // Calculate mod 97 using string arithmetic to handle large numbers
  let remainder = 0
  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString[i])) % 97
  }

  return remainder === 1
}

// BIC validation function
function validateBIC(bic: string): boolean {
  // Remove spaces and convert to uppercase
  const code = bic.replace(/\s/g, '').toUpperCase()

  // BIC format: 4 letters (bank code) + 2 letters (country code) + 2 alphanumeric (location) + optional 3 alphanumeric (branch)
  // Total length: 8 or 11 characters
  if (code.length !== 8 && code.length !== 11) {
    return false
  }

  // Check format: AAAABBCCXXX where:
  // AAAA = 4 letter bank code
  // BB = 2 letter country code
  // CC = 2 alphanumeric location code
  // XXX = optional 3 alphanumeric branch code
  const bicRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/

  if (!bicRegex.test(code)) {
    return false
  }

  // Validate country code (same as IBAN country codes)
  const validCountryCodes = [
    'AD',
    'AE',
    'AL',
    'AT',
    'AZ',
    'BA',
    'BE',
    'BG',
    'BH',
    'BR',
    'BY',
    'CH',
    'CR',
    'CY',
    'CZ',
    'DE',
    'DK',
    'DO',
    'EE',
    'EG',
    'ES',
    'FI',
    'FO',
    'FR',
    'GB',
    'GE',
    'GI',
    'GL',
    'GR',
    'GT',
    'HR',
    'HU',
    'IE',
    'IL',
    'IS',
    'IT',
    'JO',
    'KW',
    'KZ',
    'LB',
    'LC',
    'LI',
    'LT',
    'LU',
    'LV',
    'MC',
    'MD',
    'ME',
    'MK',
    'MR',
    'MT',
    'MU',
    'NL',
    'NO',
    'PK',
    'PL',
    'PS',
    'PT',
    'QA',
    'RO',
    'RS',
    'SA',
    'SE',
    'SI',
    'SK',
    'SM',
    'TN',
    'TR',
    'UA',
    'VG',
    'XK'
  ]

  const countryCode = code.slice(4, 6)
  if (!validCountryCodes.includes(countryCode)) {
    return false
  }

  // Additional validation: location code should not be '00' or '01' (reserved)
  const locationCode = code.slice(6, 8)
  if (locationCode === '00' || locationCode === '01') {
    return false
  }

  return true
}

// Add IBAN validation
yup.addMethod(yup.string, 'iban', function (message) {
  return this.test('iban', message, function (value) {
    if (!value) return true // Handle optional IBAN
    return validateIBAN(value)
  })
})

// Add BIC validation
yup.addMethod(yup.string, 'bic', function (message) {
  return this.test('bic', message, function (value) {
    if (!value) return true // Handle optional BIC
    return validateBIC(value)
  })
})

// Set default locale
setLocale({
  mixed: {
    required: 'This field is required'
  },
  string: {
    email: 'Enter a valid email address.'
  }
})

export { yup }
